import {ReactElement, CSSProperties, useMemo} from 'react';
import {Image, Link} from '@shopify/hydrogen';

import {ProductReview} from '../types';
import {formatPublishedAtDate} from '../utils';
import {PlaceholderWhiskyJPEG} from '../assets/img';

interface ProductReviewTileProps {
  review: ProductReview;
  showImage?: boolean;
  onReviewOpen?: () => void;
  onReviewEdit?: () => void;
}

export default function ProductReviewTile({
  review: {
    rating,
    title,
    description,
    reviewed_by,
    reviewed_at,
    is_own,
    product: {title: productTitle, main_image, handle},
  },
  showImage = true,
  onReviewOpen,
  onReviewEdit,
}: ProductReviewTileProps): ReactElement {
  const productImage = useMemo(
    () => main_image || PlaceholderWhiskyJPEG,
    [main_image],
  );

  return (
    <div className="reviews__item-inner">
      {showImage ? (
        <div className="reviews__item-image">
          <Link to={`/shop/${handle}`} className="reviews__item-pic">
            <picture>
              <source srcSet={productImage} />
              <Image
                src={productImage}
                width={320}
                height={400}
                alt={productTitle}
              />
            </picture>
          </Link>
          <h4 className="reviews__item-prod">
            <Link
              className="reviews__item-prod__link"
              to={`/shop/${handle}`}
              rel="noreferrer"
            >
              {productTitle}
            </Link>
          </h4>
        </div>
      ) : (
        <div></div>
      )}
      <div className="reviews__item-content">
        <div className="reviews__item-content__top">
          <h6 className="reviews__item-content__author" title={reviewed_by}>
            {reviewed_by}
          </h6>
          <div className="reviews__item-content__score">{rating}/5</div>
          <div
            className="reviews__item-content__rating rating-graph"
            style={{'--rating': rating} as CSSProperties}
          />
        </div>
        <div className="reviews__item-content__main">
          <h6 className="reviews__item-content__title">{title}</h6>
          <div className="reviews__item-content__text">{description}</div>
          {onReviewOpen && (
            <div
              role="button"
              tabIndex={0}
              onClick={onReviewOpen}
              className="reviews__item-content__view js-review-modal-opener js-modal-opener"
              data-modal-id="modal-review"
            >
              View {is_own ? 'your' : `${reviewed_by}'s`} review&nbsp;
            </div>
          )}
          {onReviewEdit && is_own && (
            <div
              role="button"
              tabIndex={0}
              onClick={onReviewEdit}
              className="reviews__item-content__view js-review-modal-opener js-modal-opener"
              data-modal-id="modal-review"
            >
              Edit review
            </div>
          )}
        </div>
        <div className="reviews__item-content__bottom">
          {formatPublishedAtDate(reviewed_at)}
        </div>
      </div>
    </div>
  );
}
